import {useState} from 'react'
import {Label, Text, Box, CounterLabel} from '@primer/react'
import {EllipsisIcon} from '@primer/octicons-react'
import type {Model} from '../../types'

export default function SidebarInfo({model}: {model: Model}) {
  const {task, properties, summary, tags} = model
  const [languagesIsOpen, setLanguagesIsOpen] = useState(false)
  const inputTokens = properties.limits.inputs.tokens
  const trainingCutoff = properties.training_data
  const languages = properties.limits.languages.filter(language => language !== null)
  const shouldTruncateLanguages = languages.length > 12
  let languagesString: string | undefined

  if (languages.length === 1) {
    languagesString = languages[0]
  } else if (shouldTruncateLanguages && !languagesIsOpen) {
    languagesString = languages.slice(0, 12).join(', ')
  } else {
    const languagesButLast = languages.slice(0, languages.length - 1).join(', ')
    languagesString = `${languagesButLast}, and ${languages[languages.length - 1]}`
  }

  const modelClass = properties.limits.rate?.model_class

  const Heading = ({title, count}: {title: string; count?: number}) => (
    <Box sx={{display: 'flex', alignItems: 'baseline', gap: 1}}>
      <Text sx={{fontWeight: 'bold', fontSize: [1, 1, 2]}}>{title}</Text>
      {count && <CounterLabel>{count}</CounterLabel>}
    </Box>
  )

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 5,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
        }}
      >
        <Heading title="About" />
        <Text sx={{lineHeight: 1.5}}>{summary}</Text>
        {tags.length > 0 && (
          <Box sx={{display: 'flex', gap: 2, flexWrap: 'wrap'}}>
            {tags.map(tag => (
              <Label key={tag} size="large" variant="secondary">
                {tag}
              </Label>
            ))}
          </Box>
        )}
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
        }}
      >
        <Heading title="Task" />
        <Box sx={{display: 'flex', gap: 2, flexWrap: 'wrap'}}>
          <Label size="large" variant="secondary">
            {task}
          </Label>
        </Box>
      </Box>

      {languages.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
          }}
        >
          <Heading title="Languages" count={languages.length} />
          <Box>
            {languagesString}{' '}
            {shouldTruncateLanguages && !languagesIsOpen && (
              <>
                {' '}
                <Box
                  as="button"
                  aria-label="Reveal all languages"
                  sx={{color: 'fg.muted', display: 'inline', cursor: 'pointer', border: 0, bg: 'transparent'}}
                  onClick={() => setLanguagesIsOpen(true)}
                >
                  <EllipsisIcon />
                </Box>
              </>
            )}
          </Box>
        </Box>
      )}

      {modelClass && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
          }}
        >
          <Heading title="Model class" />
          <Box>
            <Label>{modelClass}</Label>
          </Box>
        </Box>
      )}

      {inputTokens > 0 && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
          }}
        >
          <Heading title="Context size" />
          <Box>{inputTokens.toLocaleString()} tokens</Box>
        </Box>
      )}

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
        }}
      >
        <Heading title="Training" />
        <Box>{trainingCutoff || 'Undisclosed'}</Box>
      </Box>
    </Box>
  )
}

try{ SidebarInfo.displayName ||= 'SidebarInfo' } catch {}
try{ Heading.displayName ||= 'Heading' } catch {}